'use client'

import React, { use, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useFormStatus } from 'react-dom'
import { useCheckout, useIsMobile } from '@/lib/hooks'
import { cn, handleNumberInput } from '@/lib/utils'

import { Button, KeyChip, Rs } from '@/components/ui'
import { FloatingKeyboard } from '@/components/ui/keyboard'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { RefContext, type Refs } from '@/components/providers/ref-provider'

interface PaymentInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  defaultValue?: string
  postfix?: React.ReactNode
  isLoading?: boolean
}

export const PaymentInput = React.forwardRef<
  HTMLInputElement,
  PaymentInputProps
>(
  (
    { className, defaultValue, isLoading, postfix, value, onChange, ...props },
    ref,
  ) => {
    const { pending } = useFormStatus()
    const { fully_paid } = useCheckout()
    const { amountInputRef } = use(RefContext) as Refs
    const isMobile = useIsMobile()
    const showVirtualKeyboard = localStorage?.getItem('showVirtualKeyboard')

    // Use controlled state if value is provided, otherwise use internal state
    const [internalValue, setInternalValue] = useState(defaultValue || '')
    const inputValue = value !== undefined ? String(value) : internalValue

    React.useEffect(() => {
      amountInputRef?.current?.focus()
    }, [amountInputRef])

    const handleKeyPress = (keyValue: string) => {
      if (!amountInputRef?.current) return

      let newValue = inputValue

      if (keyValue === '⌫') {
        newValue = newValue.slice(0, -1)
      } else if (keyValue === 'clear') {
        newValue = ''
      } else {
        newValue = newValue + keyValue
      }

      // Update the input value
      amountInputRef.current.value = newValue

      // Update React state
      if (value === undefined) {
        setInternalValue(newValue)
      }

      // Call onChange if provided
      if (onChange) {
        const event = {
          target: { value: newValue },
        } as React.ChangeEvent<HTMLInputElement>
        onChange(event)
      }

      // Trigger input event to ensure form state is updated
      const event = new Event('input', { bubbles: true })
      amountInputRef.current.dispatchEvent(event)
    }

    React.useEffect(() => {
      if (amountInputRef.current && defaultValue) {
        amountInputRef.current.value = defaultValue
        if (value === undefined) {
          setInternalValue(defaultValue)
        }
      }
    }, [amountInputRef.current, defaultValue, value])

    // Sync the input value when the controlled value changes
    React.useEffect(() => {
      if (amountInputRef.current && value !== undefined) {
        amountInputRef.current.value = String(value)
      }
    }, [amountInputRef.current, value])

    return (
      <Popover defaultOpen>
        <div
          className={cn(
            'flex h-[52px] w-full items-center gap-2 overflow-hidden rounded-lg border border-gray-100 bg-white pl-2 pr-1 ring-gray-700 focus-within:bg-white focus-within:ring-2 dark:border-gray-700',
            className,
          )}
          ref={ref}
        >
          <div className="flex h-full w-full items-center gap-1">
            <span className="shrink-0 px-2">
              <Rs className="size-4" strokeWidth={1.5} />
            </span>
            <PopoverTrigger asChild>
              <input
                type="text"
                ref={amountInputRef}
                placeholder="Enter Amount"
                className="h-full w-full bg-transparent font-mono text-lg font-medium placeholder-gray-400 placeholder:font-sans placeholder:text-sm focus-visible:outline-none"
                onKeyDown={handleNumberInput}
                autoCorrect="off"
                spellCheck={false}
                value={inputValue}
                onChange={onChange}
                autoFocus
                {...props}
              />
            </PopoverTrigger>
          </div>

          <div className="flex h-full shrink-0 gap-1">
            {postfix && (
              <div className="flex h-full shrink-0 gap-1 py-1">{postfix}</div>
            )}

            <div className="flex h-full shrink-0 gap-1 py-1">
              <Button
                disabled={pending || fully_paid || isLoading}
                aria-disabled={pending || fully_paid || isLoading}
                className="h-full min-w-[132px] gap-2 rounded-md pr-2"
                tabIndex={-1}
                type="submit"
              >
                <span>{isLoading ? 'Collecting...' : 'Collect'}</span>
                {pending && <Loader2 className="size-4 animate-spin" />}

                {!isLoading && (
                  <KeyChip
                    variant="lighter"
                    shortcut="enter"
                    className="h-full"
                  />
                )}
              </Button>
            </div>
          </div>
        </div>

        {!isMobile && showVirtualKeyboard === 'true' && (
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            sideOffset={4}
          >
            <FloatingKeyboard
              onKeyPress={handleKeyPress}
              type="number"
              isNumberOnly
            />
          </PopoverContent>
        )}
      </Popover>
    )
  },
)

PaymentInput.displayName = 'PaymentInput'
